import React, { useCallback, useEffect, useState } from 'react';
import { AppState, Platform, Pressable, StyleSheet, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import useTranslation from '@/hooks/useTranslation';
import { clipboardHasImage, getClipboardImageAsFile } from '@/lib/clipboardImage';

/**
 * Native-only chip shown above the composer when the clipboard holds an image
 * (e.g. a screenshot). Tapping attaches it via the existing paste/upload path.
 */
function ClipboardImagePasteBanner({ onPasteFiles, enabled = true }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [isPasting, setIsPasting] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || Platform.OS === 'web' || Platform.OS === 'macos' || !onPasteFiles) {
      setVisible(false);
      return;
    }
    const hasImage = await clipboardHasImage();
    setVisible(hasImage);
  }, [enabled, onPasteFiles]);

  useEffect(() => {
    refresh();
    const appSub = AppState.addEventListener('change', (state) => {
      if (state === 'active') refresh();
    });

    let clipboardSub = null;
    const supportsImageClipboard =
      Platform.OS !== 'web' && Platform.OS !== 'macos';
    if (
      supportsImageClipboard &&
      typeof Clipboard.addClipboardListener === 'function'
    ) {
      clipboardSub = Clipboard.addClipboardListener(() => {
        refresh();
      });
    }

    return () => {
      appSub?.remove?.();
      if (clipboardSub) {
        if (typeof Clipboard.removeClipboardListener === 'function') {
          Clipboard.removeClipboardListener(clipboardSub);
        } else {
          clipboardSub.remove?.();
        }
      }
    };
  }, [refresh]);

  const handlePress = useCallback(async () => {
    if (isPasting || !onPasteFiles) return;
    setIsPasting(true);
    try {
      const file = await getClipboardImageAsFile();
      if (file) {
        await onPasteFiles([file]);
        setVisible(false);
      }
    } catch (error) {
      console.error('[ClipboardImagePasteBanner] paste failed:', error);
    } finally {
      setIsPasting(false);
    }
  }, [isPasting, onPasteFiles]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <View style={styles.wrapper}>
      <Pressable
        onPress={handlePress}
        disabled={isPasting}
        style={({ pressed }) => [
          styles.chip,
          {
            backgroundColor: colors.solid_color_secondary,
            borderColor: colors.border_color_secondary,
            opacity: pressed || isPasting ? 0.7 : 1,
          },
        ]}
        accessibilityRole="button"
        accessibilityLabel={t('chatInput.files.pasteFromClipboard')}
      >
        <Icon name="image" size={14} color={colors.text_color_primary} />
        <Text size="s" color="primary" style={styles.label}>
          {isPasting
            ? t('chatInput.files.pastingImage')
            : t('chatInput.files.pasteFromClipboard')}
        </Text>
      </Pressable>
      <Pressable
        onPress={handleDismiss}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={t('common.cancel')}
        style={styles.dismiss}
      >
        <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 6,
    paddingBottom: 2,
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    flexShrink: 1,
  },
  label: {
    flexShrink: 1,
  },
  dismiss: {
    padding: 6,
  },
});

export default React.memo(ClipboardImagePasteBanner);
